import { FC, useEffect, useState } from "react"
import { Activity, Building2, Megaphone, Plus, Wallet } from "lucide-react"
import { Organization } from "../../../types"
import OrgAutocomplete from "../org-autocomplete"
import PrimaryBtn from "../buttons/primary-btn"
import Tabs from "../tabs"
import ChannelsTab from "./channels-tab"
import BalanceTab from "./balance-tab"
import StatsTab from "./stats-tab"
import UnpaidLeadsTab from "./unpaid-leads-tab"
import ManageChannel from "./channels-manage"
import {
  useFindChannelsQuery,
  useGetChannelBalanceQuery
} from "../../../redux/queries"
import { useIsSuperAdmin, useIsBankAdmin, useAppSelector } from "../../hooks"
import { selectUserDetails } from "../../../redux/slices/auth"

type TabId = "channels" | "balance" | "stats" | "unpaid"

interface Props {
  orgs: Organization[]
}

const ChannelLeads: FC<Props> = ({ orgs }) => {
  const isAdmin = useIsSuperAdmin()
  const isBankAdmin = useIsBankAdmin()
  const user = useAppSelector(selectUserDetails)
  const [selectedOrgId, setSelectedOrgId] = useState<string>("")
  const [tab, setTab] = useState<TabId>("channels")
  const [selectedChannelId, setSelectedChannelId] = useState<string>("")
  const [isCreating, setIsCreating] = useState<boolean>(false)

  const { data: channels, isFetching: isFetchingChannels } =
    useFindChannelsQuery({ org_id: selectedOrgId }, { skip: !selectedOrgId })

  const {
    data: balance,
    isFetching: isFetchingBalance,
    refetch: refetchBalance
  } = useGetChannelBalanceQuery(selectedOrgId, {
    skip: !selectedOrgId
  })

  const selectedChannel = channels?.find(c => c.id === selectedChannelId)
  const hasOrgSelected = !!selectedOrgId
  const channelCount = channels?.length ?? 0
  const activeChannels =
    channels?.filter(channel => channel.status === "active").length ?? 0
  const balanceDisplay = balance?.balance_dollars ?? "0.00"

  const handleCreateNew = () => {
    setSelectedChannelId("")
    setIsCreating(true)
  }

  const handleClose = () => {
    setSelectedChannelId("")
    setIsCreating(false)
  }

  const handleSelectChannel = (id: string) => {
    setSelectedChannelId(id)
    setIsCreating(false)
  }

  useEffect(() => {
    setSelectedChannelId("")
    setIsCreating(false)
  }, [selectedOrgId])

  // Pre-select org for bank admins
  useEffect(() => {
    if (isBankAdmin && !selectedOrgId && user?.org?.id) {
      setSelectedOrgId(user.org.id)
    }
  }, [isBankAdmin, selectedOrgId, user?.org?.id])

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Channel lead controls
            </p>
            <p className="text-sm text-gray-600">
              Select an organization to manage partner channels, balances, and
              billing.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center w-full lg:w-auto">
            {!isBankAdmin && (
              <OrgAutocomplete
                orgs={orgs}
                selected={selectedOrgId}
                onChange={setSelectedOrgId}
              />
            )}
            {hasOrgSelected && tab === "channels" && isAdmin && (
              <PrimaryBtn
                onClick={handleCreateNew}
                className="flex items-center justify-center whitespace-nowrap"
              >
                <Plus size={16} className="mr-1" />
                Add New Channel
              </PrimaryBtn>
            )}
          </div>
        </div>

        {hasOrgSelected && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            {[
              {
                label: "Channels",
                value: isFetchingChannels ? "—" : channelCount,
                icon: Megaphone,
                accent: "bg-teal-50 text-teal-600"
              },
              {
                label: "Active",
                value: isFetchingChannels ? "—" : activeChannels,
                icon: Activity,
                accent: "bg-emerald-50 text-emerald-600"
              },
              {
                label: "Balance",
                value: isFetchingBalance ? "—" : `$${balanceDisplay}`,
                icon: Wallet,
                accent: "bg-sky-50 text-sky-600"
              }
            ].map(stat => (
              <div
                key={stat.label}
                className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-4 py-3"
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {stat.label}
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {stat.value}
                  </p>
                </div>
                <div
                  className={`p-2 rounded-lg shadow-sm ${stat.accent} bg-opacity-70`}
                >
                  <stat.icon size={18} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!hasOrgSelected && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-10 text-center">
          <div className="mx-auto w-12 h-12 flex items-center justify-center rounded-full bg-teal-50 text-teal-600">
            <Building2 size={22} />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">
            Choose an organization to get started
          </h3>
          <p className="mt-2 text-sm text-gray-500 max-w-2xl mx-auto">
            Pick an organization to review channel partners, balances,
            statistics, and unpaid lead billing status.
          </p>
        </div>
      )}

      {hasOrgSelected && (
        <>
          <Tabs
            currentTab={tab}
            tabs={[
              { id: "channels" as TabId, label: "Channels" },
              { id: "balance" as TabId, label: "Balance & Transactions" },
              { id: "stats" as TabId, label: "Statistics" },
              { id: "unpaid" as TabId, label: "Unpaid Leads" }
            ]}
            onChange={setTab}
            className="bg-white rounded-2xl border border-gray-200 shadow-sm px-4"
          />

          <div className="pt-4">
            {tab === "channels" && (
              <ChannelsTab
                data={channels ?? []}
                isFetching={isFetchingChannels}
                onSelect={handleSelectChannel}
                isAdmin={isAdmin}
              />
            )}
            {tab === "balance" && (
              <BalanceTab
                orgId={selectedOrgId}
                balance={balance}
                channels={channels ?? []}
                isAdmin={isAdmin}
                canManageAutoReload={isAdmin || isBankAdmin}
                canAddFunds={isAdmin || isBankAdmin}
                onBalanceRefresh={refetchBalance}
              />
            )}
            {tab === "stats" && (
              <StatsTab orgId={selectedOrgId} channels={channels ?? []} />
            )}
            {tab === "unpaid" && (
              <UnpaidLeadsTab orgId={selectedOrgId} isAdmin={isAdmin} />
            )}
          </div>
        </>
      )}

      {isAdmin && (
        <ManageChannel
          key={isCreating ? "create" : selectedChannelId}
          open={!!selectedChannelId || isCreating}
          data={isCreating ? undefined : selectedChannel}
          orgId={selectedOrgId}
          onClose={handleClose}
        />
      )}
    </div>
  )
}

export default ChannelLeads
